/* eslint-disable camelcase */
import React, { useState, useEffect, useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Checkbox,
  Button,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
} from '@material-ui/core';
import Select from 'react-select';
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
  KeyboardDateTimePicker,
  DateTimePicker,
} from '@material-ui/pickers';

import scrollTo from 'gatsby-plugin-smoothscroll';

import { format } from 'date-fns';
import DateFnsUtils from '@date-io/date-fns';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import EyeIcon from '@material-ui/icons/RemoveRedEyeTwoTone';
import OpenInNewTwoToneIcon from '@material-ui/icons/OpenInNewTwoTone';
import NavigationTwoToneIcon from '@material-ui/icons/NavigationTwoTone';

import { PergoTextField } from 'components/PergoTextField/PergoTextField';
import { CircularProgressContext } from 'components/CircularProgressOverlay';

import { JobContext } from 'context/jobContextContainer';
import { CallContext } from 'context/callContextContainer';
import { MapContext } from 'context/mapContext/mapContextContainer';
import { jobsService } from 'services/jobsService';
import { getLoginUser } from 'services/loginService';
import { computeRouteViewMapLayers } from 'utils/routeViewMapLayers';
import { getVehicleLabelForDriver } from 'utils/driverVehicleLabel';
import { sortRouteViewJobsChronologically } from 'utils/routeViewJobOrder';
import { enrichRouteViewJobsWithTripMiles, parseTripMilesFromJob } from 'utils/routeViewTripMiles';
import {
  getRouteViewGroupDriverId,
  resolveAssignedDriverId,
  shouldShowRouteViewDriverSummary,
  getRouteViewDriverSummaryLabel,
  UNASSIGNED_ROUTE_DRIVER_ID,
  isRouteDriverSelected,
} from 'utils/routeViewDriver';

const moment = require('moment-timezone');

const formatJobAddress = (location, fallbackAddr) => {
  if (location && typeof location === 'object') {
    return [location.location_name, location.addr1, location.city, location.state]
      .filter(str => str?.length > 0)
      .join(', ');
  }
  if (typeof location === 'string' && location.length > 0) {
    return location;
  }
  if (typeof fallbackAddr === 'string') {
    return fallbackAddr;
  }
  return '';
};

const shortAddress = (location, fallbackAddr) => {
  const text = formatJobAddress(location, fallbackAddr);
  if (!text) {
    return '';
  }
  const parts = text.split(',').map(part => part.trim()).filter(Boolean);
  return parts.length > 1 ? parts[0] : text;
};

const joinNameParts = (...parts) =>
  parts
    .filter(part => part != null && `${part}`.trim())
    .join(' ')
    .trim();

/** get_routeview_jobs uses flat fields; get_routemapview_jobs often uses nested passenger.{fname,lname}. */
const formatPassengerName = row => {
  const flatName = joinNameParts(row.passenger_fname, row.passenger_lname);
  if (flatName) {
    return flatName;
  }

  if (row.passenger && typeof row.passenger === 'object') {
    const nestedName = joinNameParts(
      row.passenger.fname,
      row.passenger.lname,
      row.passenger.first_name,
      row.passenger.last_name
    );
    if (nestedName) {
      return nestedName;
    }
  }

  if (typeof row.passenger === 'string' && row.passenger.trim()) {
    return row.passenger.trim();
  }

  if (row.passenger_name) {
    return `${row.passenger_name}`.trim();
  }

  return joinNameParts(row.first_name, row.last_name, row.fname, row.lname);
};

const headCells = [
  { id: 'job_id', disablePadding: true, label: 'Job #' },
  { id: 'driver', disablePadding: false, label: 'Driver' },
  { id: 'time', disablePadding: false, label: 'Reservation Time' },
  { id: 'passenger', disablePadding: false, label: 'Passenger' },
  { id: 'pickup', disablePadding: false, label: 'Pickup' },
  { id: 'dropoff', disablePadding: false, label: 'Drop Off' },
  { id: 'distance', disablePadding: false, label: 'Distance' },
  { id: 'payment_type', disablePadding: false, label: 'Payment Type' },
  { id: 'fare', disablePadding: false, label: 'Fare' },
  { id: 'status', disablePadding: false, label: 'Status' },
  { id: 'cancel', disablePadding: false, label: '' },
];

function EnhancedTableHead(props) {
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler = property => event => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map(headCell => (
          <TableCell key={headCell.id} align="center" sortDirection={orderBy === headCell.id ? order : false}>
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
};

const RouteViewTable = ({ editJob, confirmReservation, isUpdate }) => {
  const [tableData, setTableData] = useState([]);
  const [info, setInfo] = useState({ order: 'asc', orderBy: 'time' });
  const [assignDriverConfirm, setAssignDriverConfirm] = useState(false);
  const isInitialRouteViewLoadRef = useRef(true);
  const skipNextTableFetchRef = useRef(false);
  const { showCircularProgress, hideCircularProgress } = useContext(CircularProgressContext);
  const {
    driverList,
    exitJobEditing,
    setExitJobEditing,
    selectedRouteDriverId,
    setSelectedRouteDriverId,
    routeViewReservationDate,
    setRouteViewReservationDate,
    setRouteViewDriverSummaries,
    activeDriverList,
  } = useContext(JobContext);
  const { mapData, setMapData } = useContext(MapContext);
  const { siteSetting } = useContext(CallContext);
  const [reservationDate, setReservationDate] = useState(routeViewReservationDate);
  const [selectedDriverJobs, setSelectedDriverJobs] = useState([]);
  const [connectorRows, setConnectorRows] = useState([]);

  const paymentType = ['Cash', 'Card', 'Voucher', 'EHail Mobile', 'Karhoo', 'Stored Card', 'Webapp Credit'];

  const jobTableRowFormat = row => {
    const reservation_time = moment(row.reservation_time).isValid()
      ? moment
        .utc(row.reservation_time)
        .tz(siteSetting().timezone)
        .format(row.willcall_status == '1' ? 'MM/DD/YYYY' : 'MM/DD/YYYY hh:mm A')
      : '';
    let miles =
      row.distance != null && row.distance !== '' ? parseFloat(row.distance) : null;
    if (!Number.isFinite(miles)) {
      miles = parseTripMilesFromJob(row);
    }
    const distance = Number.isFinite(miles) ? miles.toFixed(2) : '';
    const payment_type = paymentType[row.tender_type];

    let status = '';
    let status_attr = '';
    row.status = parseInt(row.status);
    row.job_type = parseInt(row.job_type);

    switch (row.status) {
      case 1:
        status = row.job_type === 2 ? 'Reservation/Dispatched' : 'Dispatched';
        status_attr = 'dispatched';
        break;
      case 2:
        status = 'Accepted';
        status_attr = 'accepted';
        break;
      case 3:
        status = 'Declined';
        status_attr = 'declined';
        break;
      case 4:
        status = '@Pickup';
        status_attr = 'accepted';
        break;
      case 5:
        status = 'Picked Up';
        status_attr = 'pickup';
        break;
      case 6:
        status = 'Drop-Off';
        status_attr = 'dropoff';
        break;
      case 7:
        status = 'Pay';
        status_attr = 'pay';
        break;
      case 8:
        status = 'Complete';
        status_attr = 'complete';
        break;
      case 9:
        status = 'No Show';
        status_attr = 'cancel';
        break;
      case 10:
        status = 'Cancelled';
        status_attr = 'cancel';
        break;
      case 11:
        status = 'Backlog';
        status_attr = 'backlog';
        break;
      default:
        status = 'CFS';
        status_attr = 'cfs';
        break;
    }

    if (row.status === 0) {
      switch (row.job_type) {
        case 2:
          status = 'Reservation';
          break;
        case 3:
          status = 'E-Hail';
          break;
        case 4:
          status += ' (E-Hail)';
          break;
        default:
          break;
      }

      if (row.driver_id && parseInt(row.driver_id) > 0) {
        status_attr = 'assigned';
      }
    }
    const passengerName = formatPassengerName(row);

    return {
      ...row,
      job_id: row.id,
      time: reservation_time,
      passenger_name: passengerName,
      passenger: passengerName,
      driver_id: (() => {
        const assigned = resolveAssignedDriverId(row);
        return assigned != null ? assigned : UNASSIGNED_ROUTE_DRIVER_ID;
      })(),
      pickup: row.pickup
        ? [row.pickup.location_name, row.pickup.addr1, row.pickup.city, row.pickup.state]
          .filter(str => str?.length > 0)
          .join(', ')
        : row.start_addr,
      dropoff: row.dropoff
        ? [row.dropoff.location_name, row.dropoff.addr1, row.dropoff.city, row.dropoff.state]
          .filter(str => str?.length > 0)
          .join(', ')
        : row.end_addr,
      distance,
      payment_type,
      fare: row.fare,
      status,
      status_attr,
      job_status: row.status,
      vehicle_label:
        row.vehicle_no && row.vehicle_type
          ? `${row.vehicle_no} - ${row.vehicle_type}`
          : row.vehicle_no || row.vehicle_type || '',
    };
  };

  const processRouteViewData = tblData => {
    if (tblData.length === 0) {
      return tblData;
    }

    const sortCancelToEnd = rows => {
      const isCancelled = job => job.job_status == 3 || job.job_status == 10;
      rows.sort((a, b) =>
        (isCancelled(a) && isCancelled(b)) || (!isCancelled(a) && !isCancelled(b))
          ? 0
          : isCancelled(a) && !isCancelled(b)
            ? 1
            : -1
      );
      return rows;
    };

    const byGroup = new Map();
    tblData.forEach(row => {
      const groupId = getRouteViewGroupDriverId(row);
      if (!byGroup.has(groupId)) {
        byGroup.set(groupId, []);
      }
      byGroup.get(groupId).push(row);
    });

    const groupIds = Array.from(byGroup.keys()).sort((a, b) => {
      if (a === UNASSIGNED_ROUTE_DRIVER_ID) {
        return 1;
      }
      if (b === UNASSIGNED_ROUTE_DRIVER_ID) {
        return -1;
      }
      const labelA = driverIdToOption(a).label;
      const labelB = driverIdToOption(b).label;
      return labelA.localeCompare(labelB);
    });

    const result = [];
    groupIds.forEach(groupId => {
      const rows = sortCancelToEnd([...byGroup.get(groupId)]);
      result.push({
        isRouteView: true,
        job_count: rows.length,
        driver_id: groupId,
      });
      result.push(...rows);
    });
    return result;
  };

  const updateTable = async (reservationDateValue, { showLoading = false } = {}) => {
    if (showLoading) {
      showCircularProgress('Loading');
    }
    try {
      const data = await jobsService.get_routeview_jobs({
        order_by: info.orderBy,
        order: info.order,
        reservation_date: reservationDateValue,
      });
      if (data.data.status === 'SUCCESS') {
        const rawJobs = data.data.data || [];
        let tblData = rawJobs.map(item => jobTableRowFormat(item));
        setRouteViewDriverSummaries(buildDriverSummariesFromRows(rawJobs));
        tblData = processRouteViewData(tblData);
        setTableData(tblData);
      }
    } finally {
      if (showLoading) {
        hideCircularProgress();
      }
    }
  };

  const buildSequenceConnectorRows = (jobs, mapConnectors) => {
    const jobById = jobId =>
      jobs.find(job => String(job.id) === String(jobId) || String(job.job_id) === String(jobId));

    return mapConnectors.map(connector => {
      const fromJob = jobById(connector.fromJobId);
      const toJob = jobById(connector.toJobId);
      return {
        fromJobId: connector.fromJobId,
        toJobId: connector.toJobId,
        miles: connector.miles ?? 0,
        minutes: connector.minutes ?? 0,
        fromShort: shortAddress(fromJob?.dropoff, fromJob?.end_addr),
        toShort: shortAddress(toJob?.pickup, toJob?.start_addr),
      };
    });
  };

  const loadSelectedDriverRoute = async driverId => {
    const id = parseInt(driverId, 10);
    if (!Number.isFinite(id) || id < 0) {
      setSelectedDriverJobs([]);
      setConnectorRows([]);
      setMapData(prev => ({
        ...prev,
        routeViewData: [],
        routeViewSummary: null,
        routeViewLayers: null,
      }));
      return;
    }

    showCircularProgress('Loading');
    try {
      const data = await jobsService.get_routemapview_jobs({
        driver_id: id,
        reservation_date: reservationDate,
      });

      if (data.data.status !== 'SUCCESS') {
        return;
      }

      const jobs = sortRouteViewJobsChronologically(data.data.data || []);

      const passengerNameByJobId = tableData.reduce((acc, r) => {
        if (!r.isRouteView && r.job_id) {
          const name =
            typeof r.passenger_name === 'string'
              ? r.passenger_name
              : typeof r.passenger === 'string'
                ? r.passenger
                : '';
          if (name) {
            acc[r.job_id] = name;
          }
        }
        return acc;
      }, {});

      const distanceByJobId = tableData.reduce((acc, r) => {
        if (!r.isRouteView && r.job_id) {
          const miles = parseTripMilesFromJob(r) ?? parseFloat(r.distance);
          if (Number.isFinite(miles) && miles > 0) {
            acc[r.job_id] = miles;
          }
        }
        return acc;
      }, {});

      const jobsWithMiles = await enrichRouteViewJobsWithTripMiles(jobs, distanceByJobId);
      const routeViewLayers = await computeRouteViewMapLayers(jobsWithMiles, siteSetting().timezone);

      const formattedJobs = jobsWithMiles.map(item => {
        const formatted = jobTableRowFormat(item);
        const resolvedName =
          formatted.passenger_name ||
          passengerNameByJobId[item.id] ||
          passengerNameByJobId[String(item.id)] ||
          '';
        return {
          ...formatted,
          passenger_name: resolvedName,
          passenger: resolvedName,
        };
      });

      const connectors = buildSequenceConnectorRows(jobsWithMiles, routeViewLayers.connectors);
      const serviceMiles = formattedJobs.reduce((sum, job) => sum + parseFloat(job.distance || 0), 0);
      const connectorMiles = connectors.reduce((sum, row) => sum + parseFloat(row.miles || 0), 0);
      const firstTime = formattedJobs[0]?.time;
      const lastTime = formattedJobs[formattedJobs.length - 1]?.time;
      const driverLabel =
        id === UNASSIGNED_ROUTE_DRIVER_ID ? 'Unassigned' : driverIdToOption(id).label;

      setSelectedDriverJobs(formattedJobs);
      setConnectorRows(connectors);
      setExitJobEditing(!exitJobEditing);
      scrollTo('#main-wrapper');

      setMapData(prev => ({
        ...prev,
        routeViewData: [...jobsWithMiles],
        routeViewLayers,
        routeViewSummary: {
          driverId: id,
          driverLabel,
          trips: formattedJobs.length,
          serviceMiles,
          connectorMiles,
          window: firstTime && lastTime ? `${firstTime} - ${lastTime}` : '',
          vehicleLabel: getVehicleLabelForDriver(
            activeDriverList,
            id,
            formattedJobs[0]?.vehicle_label
          ),
        },
      }));
    } finally {
      hideCircularProgress();
    }
  };

  useEffect(() => {
    setRouteViewReservationDate(reservationDate);
  }, [reservationDate]);

  useEffect(() => {
    if (!isRouteDriverSelected(selectedRouteDriverId)) {
      if (skipNextTableFetchRef.current) {
        skipNextTableFetchRef.current = false;
        return;
      }
      const showLoading = isInitialRouteViewLoadRef.current;
      isInitialRouteViewLoadRef.current = false;
      updateTable(reservationDate, { showLoading });
    }
  }, [info, isUpdate, reservationDate, selectedRouteDriverId]);

  useEffect(() => {
    if (isRouteDriverSelected(selectedRouteDriverId)) {
      return undefined;
    }

    const refreshIntervalId = setInterval(() => {
      updateTable(reservationDate);
    }, 20000);

    return () => clearInterval(refreshIntervalId);
  }, [selectedRouteDriverId, reservationDate, info, isUpdate]);

  useEffect(() => {
    if (isRouteDriverSelected(selectedRouteDriverId)) {
      loadSelectedDriverRoute(selectedRouteDriverId);
    } else {
      setSelectedDriverJobs([]);
      setConnectorRows([]);
      setMapData(prev => ({
        ...prev,
        routeViewData: [],
        routeViewSummary: null,
        routeViewLayers: null,
      }));
    }
  }, [selectedRouteDriverId, reservationDate]);

  const handleRequestSort = (event, property) => {
    const isAsc = info.orderBy === property && info.order === 'asc';
    setInfo({
      ...info,
      order: isAsc ? 'desc' : 'asc',
      orderBy: property,
    });
  };

  const handleEditJob = jobId => {
    editJob(jobId);
  };

  const handleConfirmClick = (jobInfo, confirm) => {
    if (jobInfo) {
      confirmReservation([jobInfo], confirm);
    }
  };

  const onClickDriverAssign = async (jobId, driverId, index) => {
    const selDriver = driverList.filter(item => item.id === driverId);
    if (selDriver?.length > 0) {
      showCircularProgress('Assigning');
      const res = await jobsService.assign_driver_to_job(jobId, driverId, true);
      if (res && res?.data?.status === 'SUCCESS') {
        updateTable(reservationDate);
      }
      hideCircularProgress();
    }
  };

  const onChangeReservation = async (jobId, reservation_time, index) => {
    reservation_time = moment
      .tz(moment(reservation_time).format('YYYY-MM-DD HH:mm:ss'), siteSetting().timezone)
      .utc()
      .format('YYYY-MM-DD HH:mm:ss');

    showCircularProgress('Updating');
    const res = await jobsService.change_reservation_time(jobId, reservation_time);
    hideCircularProgress();
    if (res && res?.data?.status === 'SUCCESS') {
      updateTable(reservationDate);
    }
  };

  const handleDriverAssign = async () => {
    setAssignDriverConfirm(false);
    const { jobId, driverId } = assignDriverConfirm;
    showCircularProgress('Loading');
    const res = await jobsService.assign_driver_to_job(jobId, driverId, true);
    hideCircularProgress();
    if (res && res?.data?.status === 'SUCCESS') {
      updateTable(reservationDate);
    }
  };

  const handleRouteMapView = async driver_id => {
    setSelectedRouteDriverId(driver_id);
  };

  const driverIdToOption = driverId => {
    if (driverId < 0) {
      return {
        value: -1,
        label: '',
      };
    }
    const selDriver = driverList.filter(item => item.id == driverId);
    if (selDriver.length > 0) {
      return {
        value: selDriver[0].value,
        label: selDriver[0].label,
      };
    }
    return {
      value: -1,
      label: '',
    };
  };

  const buildDriverSummariesFromRows = tblData => {
    if (!tblData?.length) {
      return [];
    }

    const byDriver = new Map();

    tblData.forEach(row => {
      if (row.isRouteView) {
        return;
      }
      const driverId = resolveAssignedDriverId(row);
      if (!driverId) {
        return;
      }
      if (!byDriver.has(driverId)) {
        byDriver.set(driverId, {
          driver_id: driverId,
          job_count: 0,
          vehicle_id: null,
        });
      }
      const entry = byDriver.get(driverId);
      entry.job_count += 1;
      const vehicleId = parseInt(row.vehicle_id, 10);
      if (Number.isFinite(vehicleId) && vehicleId > 0) {
        entry.vehicle_id = vehicleId;
      }
    });

    const assignedSummaries = Array.from(byDriver.values())
      .map(entry => ({
        driver_id: entry.driver_id,
        job_count: entry.job_count,
        vehicle_id: entry.vehicle_id,
        driver_label: driverIdToOption(entry.driver_id).label,
      }))
      .filter(entry => shouldShowRouteViewDriverSummary(entry, driverList));

    return assignedSummaries.sort((a, b) =>
      (a.driver_label || '').localeCompare(b.driver_label || '')
    );
  };

  const clearRouteViewSelection = () => {
    setSelectedRouteDriverId(null);
    setSelectedDriverJobs([]);
    setConnectorRows([]);
    setMapData(prev => ({
      ...prev,
      routeViewData: [],
      routeViewSummary: null,
      routeViewLayers: null,
    }));
  };

  const resetFilter = async () => {
    skipNextTableFetchRef.current = true;
    setReservationDate(null);
    setRouteViewReservationDate(null);
    clearRouteViewSelection();
    await updateTable(null, { showLoading: true });
  };

  const renderSequenceView = () => {
    const sequenceVehicleLabel = getVehicleLabelForDriver(
      activeDriverList,
      selectedRouteDriverId,
      selectedDriverJobs[0]?.vehicle_label
    );

    return (
    <SequenceTableWrapper>
      <div className="sequence-detail-header">
        <div>
          <div className="sequence-detail-heading">ROUTE SEQUENCE</div>
          <div className="sequence-detail-subheading">
            Jobs in order, with the connector distance between each trip.
          </div>
        </div>
        <div className="sequence-detail-summary">
          {sequenceVehicleLabel ? <span>{sequenceVehicleLabel}</span> : null}
          {mapData.routeViewSummary?.window ? <span>{mapData.routeViewSummary.window}</span> : null}
          {mapData.routeViewSummary?.connectorMiles != null ? (
            <span>{mapData.routeViewSummary.connectorMiles.toFixed(1)} connector miles</span>
          ) : null}
        </div>
      </div>
      <div className="sequence-table">
        <div className="sequence-head">
          <div>Job #</div>
          <div>Time</div>
          <div>Passenger</div>
          <div>Pickup</div>
          <div>Drop Off</div>
          <div>Trip Miles</div>
        </div>
        {selectedDriverJobs.map((row, index) => {
          const connector = connectorRows[index];
          return (
            <React.Fragment key={`${row.job_id}_${index}`}>
              <div className="sequence-row route-row">
                <div>{row.job_id}</div>
                <div>{row.time}</div>
                <div>{row.passenger_name}</div>
                <div>{row.pickup}</div>
                <div>{row.dropoff}</div>
                <div>{row.distance}</div>
              </div>
              {connector ? (
                <div className="sequence-row connector-row">
                  <div className="connector-label">
                    {`Travel from "${connector.fromShort}" to next pickup "${connector.toShort}" : ${connector.miles.toFixed(1)} miles / ${Math.round(connector.minutes)} min`}
                  </div>
                </div>
              ) : null}
            </React.Fragment>
          );
        })}
      </div>
    </SequenceTableWrapper>
    );
  };

  const hasSelectedRouteDriver = isRouteDriverSelected(selectedRouteDriverId);

  return (
    <TableContainer
      onClick={() => hasSelectedRouteDriver && setSelectedRouteDriverId(null)}
    >
      {!hasSelectedRouteDriver ? (
        <TableSearch onClick={e => e.stopPropagation()}>
          <div className="reservation-table-search--container">
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
              <KeyboardDatePicker
                inputVariant="outlined"
                format="yyyy/MM/dd"
                ampm
                label="Reservation Date"
                value={reservationDate ? moment(reservationDate).format('MM/DD/YYYY hh:mm A') : null}
                onChange={e => {
                  setReservationDate(format(e, 'yyy-MM-dd'));
                }}
              />
            </MuiPickersUtilsProvider>
            <Button
              className="reservation-table-search--button"
              onClick={() => {
                skipNextTableFetchRef.current = true;
                clearRouteViewSelection();
                updateTable(reservationDate, { showLoading: true });
              }}
            >
              Search
            </Button>
            <Button className="reservation-table-search--button" onClick={resetFilter}>
              Reset
            </Button>
          </div>
        </TableSearch>
      ) : null}

      {hasSelectedRouteDriver ? (
        <div onClick={e => e.stopPropagation()}>{renderSequenceView()}</div>
      ) : (
      <Table aria-label="job table" onClick={e => e.stopPropagation()}>
        <EnhancedTableHead order={info.order} orderBy={info.orderBy} onRequestSort={handleRequestSort} />

        <TableBody>
          {tableData.map((row, index) => {
            const labelId = `enhanced-table-checkbox-${index}`;

            if (row.isRouteView) {
              return (
                <TableRow className="reservation-route-view-row">
                  <TableCell colSpan={11}>
                    {row.driver_id > 0 ? driverIdToOption(row.driver_id).label : 'Unassigned'} / Job Count :{' '}
                    {row.job_count}
                    <NavigationTwoToneIcon
                      onClick={() => handleRouteMapView(row.driver_id)}
                      style={{ marginLeft: 5, marginTop: '-3px', cursor: 'pointer' }}
                    />
                  </TableCell>
                </TableRow>
              );
            }

            return (
              <TableRow
                className={`job-status-${row.status_attr}`}
                hover
                role="checkbox"
                tabIndex={-1}
                key={`${row.job_id}_${index}`}
              >
                <TableCell
                  align="center"
                  component="th"
                  id={labelId}
                  scope="row"
                  padding="none"
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleEditJob(row.job_id)}
                >
                  {row.job_id}
                </TableCell>
                <TableCell align="center">
                  {row.willcall_status == '1' ? (
                    <></>
                  ) : row.job_status >= 2 ? (
                    row.driver_id > 0 ? (
                      driverIdToOption(parseInt(row.driver_id)).label
                    ) : (
                      ''
                    )
                  ) : (
                    <DriverSelect
                      classNamePrefix="driver-select"
                      value={driverIdToOption(parseInt(row.driver_id))}
                      onChange={selectedOption => onClickDriverAssign(row.job_id, selectedOption.value, index)}
                      options={
                        driverList?.length > 0
                          ? driverList.map(item => ({
                            value: item.id,
                            label: item.label,
                          }))
                          : []
                      }
                    />
                  )}
                </TableCell>
                {/* <TableCell align="center">
                  {row.time}
                  {row.willcall_status == '1' ? <span style={{ color: 'red' }}>&nbsp;(Will Call)</span> : ''}
                </TableCell> */}
                <TableCell
                  align="center"
                  className={`col-reservation-time ${row.willcall_status == '1' ? 'willcall-time' : ''}`}
                >
                  <MuiPickersUtilsProvider utils={DateFnsUtils}>
                    <DateTimePicker
                      format={row.willcall_status == '1' ? 'yyyy/MM/dd ??:??' : 'yyyy/MM/dd hh:mm a'}
                      value={row.time}
                      onChange={e => onChangeReservation(row.job_id, e, index)}
                    />
                  </MuiPickersUtilsProvider>
                </TableCell>
                <TableCell align="center">{row.passenger_name || row.passenger}</TableCell>
                <TableCell align="center">{row.pickup}</TableCell>
                <TableCell align="center">{row.dropoff}</TableCell>
                <TableCell align="center">{row.distance}</TableCell>
                <TableCell align="center">
                  {row.payment_type}
                  {parseFloat(row.booking_deposit_amount) > 0 && row.booking_deposit_status == 0 ? (
                    <>
                      <br />
                      <span style={{ color: 'red' }}>(Balance Due)</span>
                    </>
                  ) : (
                    <></>
                  )}
                  {parseFloat(row.booking_deposit_amount) > 0 && (
                    <div className="col-redirect-icon">
                      <a
                        href={`/invoice?token=${siteSetting()?.settings?.reservation_token}&job_id=${row.job_id}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <OpenInNewTwoToneIcon fontSize="small" />
                      </a>
                    </div>
                  )}
                </TableCell>
                <TableCell align="center">{row.fare}</TableCell>
                {row.status === 'Reservation' && (row.reservation_confirmed == 0 || row.reservation_confirmed == 3) ? (
                  <TableCell align="center" className="text-warning">
                    Confirmed
                    <br />
                    Reservation?
                  </TableCell>
                ) : (
                  <TableCell align="center">
                    {row.status}
                    {row.driver_id <= 0 && row.status < 2 ? (
                      <>
                        <br />
                        No Driver's Assigned
                      </>
                    ) : (
                      ''
                    )}
                    {row.status === 'No Show' && (
                      <EyeIcon
                        color="primary"
                        style={{ fontSize: 20, marginLeft: 5, marginBottom: 3, cursor: 'pointer' }}
                      />
                    )}
                  </TableCell>
                )}
                <TableCell align="center">
                  {row.job_status === 3 || row.job_status >= 8 ? (
                    ''
                  ) : (
                    <>
                      {row.status === 'Reservation' &&
                        (row.reservation_confirmed == 0 || row.reservation_confirmed == 3) && (
                          <Button
                            className="job-table--action-icon color-success m-1"
                            variant="contained"
                            onClick={() => handleConfirmClick(row, 'confirm')}
                          >
                            <CheckIcon fontSize="small" />
                          </Button>
                        )}
                      <Button
                        className="job-table--action-icon m-1"
                        variant="contained"
                        color="secondary"
                        onClick={() =>
                          handleConfirmClick(
                            row,
                            row.status === 'Reservation' &&
                              (row.reservation_confirmed == 0 || row.reservation_confirmed == 3)
                              ? 'decline'
                              : 'cancel'
                          )
                        }
                      >
                        <CloseIcon fontSize="small" />
                      </Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      )}
      <Dialog open={!!assignDriverConfirm} onClose={() => setAssignDriverConfirm(false)}>
        <DialogTitle id="alert-dialog-title">Are you sure?</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {`Do you really want to assign ${assignDriverConfirm?.driverLabel} driver to Job #${assignDriverConfirm?.jobId}`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDriverAssign} color="primary" autoFocus>
            Yes
          </Button>
          <Button onClick={() => setAssignDriverConfirm(false)} color="primary" autoFocus>
            No
          </Button>
        </DialogActions>
      </Dialog>
    </TableContainer>
  );
};

export default RouteViewTable;

const DriverSelect = styled(Select)`
  min-width: 150px;

  .driver-select__control {
    border: none;
    border-bottom: 1px solid #888;
    border-radius: 0;
    background: transparent;
    text-align: left;
    box-shadow: none;
    font-size: 13px;
    min-height: 0;
  }

  .driver-select__indicator {
    padding: 3px 5px;
  }

  .driver-select__menu {
    text-align: left;
    min-width: 200px;
  }
`;
const SequenceTableWrapper = styled.div`
  .sequence-detail-header {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    flex-wrap: wrap;
    margin-bottom: 10px;
  }

  .sequence-detail-heading {
    color: #0c4765;
    font-size: 14px;
    font-weight: 700;
    white-space: nowrap;
  }

  .sequence-detail-subheading {
    margin-top: 4px;
    color: #67757d;
    font-size: 13px;
  }

  .sequence-detail-summary {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    justify-content: flex-end;

    span {
      padding: 6px 10px;
      border: 1px solid #cbd5da;
      background: #ffffff;
      color: #56656e;
      font-size: 12px;
    }
  }

  .sequence-table {
    border: 1px solid #cfd8dd;
    background: #ffffff;
  }

  .sequence-head,
  .route-row {
    display: grid;
    grid-template-columns: 100px 168px 170px 1.2fr 1.2fr 100px;
  }

  .sequence-head {
    background: #d8d8d8;
    color: #26353d;
    font-weight: 700;
    font-size: 13px;
  }

  .sequence-head > div,
  .route-row > div {
    padding: 12px 10px;
    border-right: 1px solid #c9d1d6;
  }

  .sequence-head > div:nth-child(2),
  .route-row > div:nth-child(2) {
    white-space: nowrap;
  }

  .sequence-head > div {
    white-space: nowrap;
  }

  .sequence-head > div:last-child,
  .route-row > div:last-child {
    border-right: 0;
  }

  .route-row {
    background: #dff6df;
    border-top: 1px solid #c6ddc6;
    font-size: 13px;
  }

  .connector-row {
    border-top: 1px solid #d3ddd3;
    background: #0a8f08;
    color: #ffffff;
  }

  .connector-label {
    padding: 10px 14px;
    font-size: 13px;
    font-weight: 700;
    grid-column: 1 / -1;
  }
`;

const TableSearch = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  .reservation-table-search--container {
    padding-top: 5px;
    display: flex;
    justify-content: center;
    align-items: center;

    .MuiInputLabel-outlined {
      font-size: 14px;
      transform: translate(14px, 12px) scale(1);
      background: transparent;
      padding: 0 3px;

      &.MuiInputLabel-shrink {
        transform: translate(14px, -5px) scale(0.75);
      }
    }

    .MuiOutlinedInput-input {
      padding: 10px;
      font-size: 14px;
      height: auto;
    }

    .reservation-table-search--button,
    .reservation-table-print--button {
      margin-left: 15px;
      border-radius: 0;
      background: var(--blue-main-color);
      color: white;
      height: 39px;
    }
  }
`;
